import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, FileText, Loader2, Maximize2, Minimize2, Trash2, X } from 'lucide-react';
import { invoicesApi } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { emitAppEvent } from '@/utils/appEvents';
import { formatDateOnly } from '@/utils/progress';
import { formatCurrency, formatDateTime } from '@/utils/currency';
import {
  invoiceStatusLabels, invoiceStatusBadgeClasses, paymentStatusLabels,
} from './invoiceStatus';
import { InvoiceAttachments } from './InvoiceAttachments';
import { InvoiceStatusTimeline } from './InvoiceStatusTimeline';
import { InvoiceComments } from './InvoiceComments';
import { InvoiceReviewActions } from './InvoiceReviewActions';
import type {
  InvoiceComment, InvoiceDetail, InvoiceFile, InvoiceStatusLogEntry, InvoiceTransitionAction,
} from '@/types';

/**
 * Rechnungs-Detail als Drawer (Outlet-Kind der RechnungenPage): das Grid
 * bleibt gemountet — Filter, Scrollposition und Draft-Zeilen überleben.
 * Daten/Handler wie früher RechnungenDetailPage; Stammdaten-Bearbeiten
 * läuft jetzt in den Grid-Zellen, Notizen sind ein Autosave-Feld.
 */
export function InvoiceDetailDrawer() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isOffice, isFinanzen } = useAuth();
  const isNew = id === 'new';

  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [statusLog, setStatusLog] = useState<InvoiceStatusLogEntry[]>([]);
  const [comments, setComments] = useState<InvoiceComment[]>([]);
  const [attachments, setAttachments] = useState<InvoiceFile[]>([]);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [error, setError] = useState('');
  const [isWide, setIsWide] = useState(false);
  const [notes, setNotes] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [notesError, setNotesError] = useState('');
  const [isBusy, setIsBusy] = useState(false);

  const close = () => navigate('/rechnungen', { replace: true });

  // Load-Key: Mount/ID-Wechsel UND manuelle Reloads (nach Aktionen) feuern.
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    setIsLoading(true);
    setError('');
    invoicesApi
      .getById(Number(id))
      .then((response) => {
        if (cancelled) return;
        setInvoice(response.data.data.invoice);
        setStatusLog(response.data.data.status_log);
        setComments(response.data.data.comments);
        setAttachments(response.data.data.attachments ?? []);
        setNotes(response.data.data.invoice.notes ?? '');
      })
      .catch(() => {
        if (!cancelled) setError('Rechnung nicht gefunden');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew, reloadKey]);

  // Esc schließt den Drawer (Backdrop-Klick ebenfalls).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleTransition = async (action: InvoiceTransitionAction, comment?: string) => {
    if (!invoice) return;
    setIsBusy(true);
    try {
      await invoicesApi.transition(invoice.id, action, comment);
      emitAppEvent('rechnungen:changed');
      reload();
    } catch (err) {
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Statuswechsel');
    } finally {
      setIsBusy(false);
    }
  };

  const handleAddComment = async (comment: string, isInternal: boolean) => {
    if (!invoice) return;
    await invoicesApi.addComment(invoice.id, comment, isInternal);
    reload();
  };

  const handleDelete = () => {
    if (!invoice) return;
    if (!window.confirm('Möchten Sie diese Rechnung wirklich löschen?')) return;
    invoicesApi
      .remove(invoice.id)
      .then(() => {
        emitAppEvent('rechnungen:changed');
        navigate('/rechnungen', { replace: true });
      })
      .catch((err) => setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Löschen'));
  };

  // Doku/Notizen autosave (blur) — editierbar in den Status mit
  // 'notes' in der EDITABLE-Whitelist (erfasst, formal_geprueft,
  // korrektur_erforderlich).
  const canEditNotes = isOffice && ['erfasst', 'formal_geprueft', 'korrektur_erforderlich'].includes(invoice?.status ?? '');
  const handleNotesBlur = () => {
    if (!invoice || !canEditNotes) return;
    const next = notes.trim() || null;
    if (next === (invoice.notes ?? null)) return;
    setIsSavingNotes(true);
    setNotesError('');
    invoicesApi
      .update(invoice.id, { notes: next })
      .then((response) => {
        const saved = response.data.data as { notes?: string | null };
        setInvoice((prev) => (prev ? { ...prev, notes: saved.notes ?? next } : prev));
        emitAppEvent('rechnungen:changed');
      })
      .catch((err) => setNotesError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Speichern'))
      .finally(() => setIsSavingNotes(false));
  };

  // Alte Bookmarks /reviews auf /rechnungen/new: Anlegen läuft im Grid.
  if (isNew) {
    return (
      <>
        <div className="absolute inset-0 z-30 bg-gray-900/20" onClick={close} />
        <div className="absolute right-0 top-0 z-40 flex h-full w-full max-w-md flex-col border-l border-gray-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
            <h2 className="font-semibold text-gray-900">Neue Rechnung</h2>
            <button onClick={close} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" title="Schließen">
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
            <AlertCircle className="h-8 w-8 text-gray-400" />
            <p className="text-gray-700">Neue Rechnungen werden direkt in der Tabelle erfasst.</p>
            <button
              onClick={close}
              className="btn btn-secondary inline-flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Zur Tabelle
            </button>
          </div>
        </div>
      </>
    );
  }

  const canDelete = isOffice && (invoice?.status === 'erfasst' || invoice?.status === 'abgelehnt');
  const canReplaceFile = isOffice && (invoice?.status === 'erfasst' || invoice?.status === 'korrektur_erforderlich');

  return (
    <>
      <div className="absolute inset-0 z-30 bg-gray-900/20" onClick={close} />
      <div
        className={`absolute right-0 top-0 z-40 flex h-full flex-col border-l border-gray-200 bg-white shadow-2xl transition-all ${
          isWide ? 'w-full max-w-5xl' : 'w-full max-w-3xl'
        }`}
      >
        {/* Drawer-Kopf */}
        <div className="flex items-center justify-between gap-2 border-b border-gray-200 px-5 py-3">
          <button
            onClick={close}
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
            title="Zurück zur Tabelle"
          >
            <ArrowLeft className="h-4 w-4" />
            Tabelle
          </button>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsWide((w) => !w)}
              className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              title={isWide ? 'Schmaler' : 'Breiter (für PDF)'}
            >
              {isWide ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            <button
              onClick={close}
              className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              title="Schließen (Esc)"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Inhalt */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
            </div>
          ) : error && !invoice ? (
            <div className="card p-8 text-center">
              <AlertCircle className="mx-auto mb-3 h-8 w-8 text-red-500" />
              <p className="mb-4 text-gray-700">{error}</p>
              <button onClick={close} className="btn btn-secondary inline-flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Zurück zur Tabelle
              </button>
            </div>
          ) : invoice ? (
            <>
              {error && (
                <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {error}
                </div>
              )}

              {/* Kopf: Nummer + Status + Review-Aktionen */}
              <div className="card p-5 mb-5">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="truncate text-xl font-bold text-gray-900">{invoice.invoice_number}</h2>
                    <p className="text-sm text-gray-500">
                      {invoice.direction === 'eingang' ? 'Eingangsrechnung' : 'Ausgangsrechnung'}
                      {invoice.description ? ` · ${invoice.description}` : ''}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${invoiceStatusBadgeClasses[invoice.status as keyof typeof invoiceStatusBadgeClasses]}`}>
                      {invoiceStatusLabels[invoice.status as keyof typeof invoiceStatusLabels]}
                    </span>
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        invoice.payment_status === 'bezahlt' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {paymentStatusLabels[invoice.payment_status]}
                    </span>
                  </div>
                </div>
                <InvoiceReviewActions
                  invoice={invoice}
                  isOffice={isOffice}
                  isFinanzen={isFinanzen}
                  isBusy={isBusy}
                  onTransition={handleTransition}
                />
              </div>

              {/* Stammdaten — Bearbeiten läuft im Grid (Zuordnungen, Beträge, Termine). */}
              <div className="card p-5 mb-5">
                <div className="mb-4 flex items-start justify-between gap-3">
                  <h2 className="text-lg font-semibold text-gray-900">Stammdaten</h2>
                  {canDelete && (
                    <button onClick={handleDelete} className="btn btn-danger inline-flex items-center gap-2">
                      <Trash2 className="h-4 w-4" />
                      Löschen
                    </button>
                  )}
                </div>
                <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="text-gray-500">Rechnungsnummer</dt>
                    <dd className="text-gray-900">{invoice.invoice_number}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">{invoice.direction === 'eingang' ? 'Lieferant' : 'Empfänger'}</dt>
                    <dd className="text-gray-900">
                      {invoice.contact_id && invoice.contact_name ? (
                        <Link to={`/contacts/${invoice.contact_id}`} className="text-primary-600 hover:text-primary-700">
                          {invoice.contact_name}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Bauvorhaben</dt>
                    <dd className="text-gray-900">
                      {invoice.bauvorhaben_id && invoice.bauvorhaben_name ? (
                        <Link to={`/bauvorhaben/${invoice.bauvorhaben_id}`} className="text-primary-600 hover:text-primary-700">
                          {invoice.bauvorhaben_name}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Gewerk</dt>
                    <dd className="text-gray-900">{invoice.gewerk_name || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Betrag (brutto)</dt>
                    <dd className="font-semibold text-gray-900">{formatCurrency(invoice.amount_gross)}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Netto / MwSt</dt>
                    <dd className="text-gray-900">
                      {invoice.amount_net != null ? formatCurrency(invoice.amount_net) : '—'}
                      {' / '}
                      {invoice.amount_tax != null ? formatCurrency(invoice.amount_tax) : '—'}
                      {invoice.tax_rate_percent != null ? ` (${invoice.tax_rate_percent} %)` : ''}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Rechnungsdatum</dt>
                    <dd className="text-gray-900">{formatDateOnly(invoice.invoice_date) || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Fällig am</dt>
                    <dd className="text-gray-900">{formatDateOnly(invoice.due_date) || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-500">Erfasst von</dt>
                    <dd className="text-gray-900">
                      {invoice.created_by_name || (invoice.source === 'email' ? 'E-Mail-Eingang' : '—')} · {formatDateTime(invoice.created_at)}
                    </dd>
                  </div>
                  {invoice.source === 'email' && (
                    <div>
                      <dt className="text-gray-500">Quelle</dt>
                      <dd className="text-gray-900">E-Mail-Eingang (automatisch erfasst)</dd>
                    </div>
                  )}
                  {invoice.lexware_exported_at && (
                    <div>
                      <dt className="text-gray-500">Lexware-Übergabe</dt>
                      <dd className="text-gray-900">
                        {formatDateTime(invoice.lexware_exported_at)}
                        {invoice.lexware_exported_by_name ? ` von ${invoice.lexware_exported_by_name}` : ''}
                      </dd>
                    </div>
                  )}
                  <div className="sm:col-span-2">
                    <dt className="mb-1 text-gray-500">
                      Dokumentation
                      {isSavingNotes && <span className="ml-2 text-xs text-gray-400">speichert…</span>}
                    </dt>
                    {canEditNotes ? (
                      <>
                        <textarea
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          onBlur={handleNotesBlur}
                          rows={3}
                          placeholder="Dokumentation / Bericht…"
                          className={`input text-sm ${notesError ? 'ring-1 ring-red-500' : ''}`}
                        />
                        {notesError && <p className="mt-1 text-xs text-red-600">{notesError}</p>}
                      </>
                    ) : (
                      <dd className="whitespace-pre-wrap text-gray-900">{invoice.notes || '—'}</dd>
                    )}
                  </div>
                </dl>
              </div>

              {/* Anlagen (Phase 3): mehrere Dateien — Bilder mit Thumbnail
                  + Lightbox, PDF mit Inline-Vorschau, Office/CSV Download. */}
              <div className="card p-5 mb-5">
                <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <FileText className="h-5 w-5 text-gray-400" />
                  Anlagen
                </h2>
                <InvoiceAttachments
                  invoiceId={invoice.id}
                  attachments={attachments}
                  canManage={canReplaceFile}
                  onChanged={() => {
                    emitAppEvent('rechnungen:changed');
                    reload();
                  }}
                  onError={setError}
                />
              </div>

              {/* Status-Timeline */}
              <div className="card p-5 mb-5">
                <h2 className="mb-4 text-lg font-semibold text-gray-900">Statusverlauf</h2>
                <InvoiceStatusTimeline entries={statusLog} />
              </div>

              {/* Kommentare */}
              <div className="card p-5">
                <h2 className="mb-4 text-lg font-semibold text-gray-900">Kommentare</h2>
                <InvoiceComments comments={comments} isOffice={isOffice} onAdd={handleAddComment} />
              </div>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}